/**
 * ImageStore - Dexie.jsベースのIndexedDB画像管理
 * 画像データをBlobとして効率的に保存
 */

import Dexie, { type Table } from 'dexie'
import { logger } from '../lib/logger'

export interface StoredImage {
  id: string
  blob: Blob
  thumbnail?: Blob
  createdAt: Date
  metadata?: {
    width: number
    height: number
    mimeType: string
  }
}

// Dexieデータベースクラス
class ImageDatabase extends Dexie {
  images!: Table<StoredImage, string>

  constructor() {
    super('ParaParaViewDB')
    
    this.version(1).stores({
      images: 'id, createdAt' // id をプライマリキー、createdAt をインデックスに
    })
  }
}

export class ImageStore {
  private db: ImageDatabase
  private readonly MAX_IMAGE_SIZE = 5 * 1024 * 1024 // 5MB

  constructor() {
    this.db = new ImageDatabase()
  }

  async init(): Promise<void> {
    // Dexieは自動的に初期化されるため、明示的な初期化は不要
    // ただし、データベースが開けることを確認
    try {
      await this.db.open()
      // Database initialized with Dexie
    } catch (error) {
      // テスト環境では初期化済みの場合があるので無視
      if (this.db.isOpen()) {
        return
      }
      // 実際のエラーの場合は再スロー
      throw error
    }
  }

  /**
   * 画像を保存
   */
  async saveImage(base64Data: string, id?: string): Promise<string> {
    try {
      const imageId = id || crypto.randomUUID()
      
      // Base64をBlobに変換
      const response = await fetch(base64Data)
      const blob = await response.blob()
      
      // サイズチェック
      if (blob.size > this.MAX_IMAGE_SIZE) {
        throw new Error(`Image size exceeds ${this.MAX_IMAGE_SIZE / 1024 / 1024}MB limit`)
      }
      
      // サムネイル生成（オプション）
      const thumbnail = await this.generateThumbnail(base64Data)
      
      // 画像メタデータ取得
      const metadata = await this.getImageMetadata(base64Data)
      
      const storedImage: StoredImage = {
        id: imageId,
        blob,
        thumbnail,
        createdAt: new Date(),
        metadata
      }
      
      // Dexieで保存（put は upsert として動作）
      await this.db.images.put(storedImage)
      
      // Image saved: ${imageId}, size: ${(blob.size / 1024).toFixed(2)}KB
      return imageId
      
    } catch (error) {
      logger.error('[ImageStore] Failed to save image:', error)
      throw error
    }
  }

  /**
   * 画像を取得（Base64形式）
   */
  async getImage(id: string): Promise<string | null> {
    try {
      const storedImage = await this.db.images.get(id)
      
      if (!storedImage) {
        return null
      }
      
      // BlobをBase64に変換
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(storedImage.blob)
      })
      
    } catch (error) {
      logger.error('[ImageStore] Failed to get image:', error)
      return null
    }
  }

  /**
   * 画像Blobを取得（URL生成用）
   */
  async getImageBlob(id: string): Promise<Blob | null> {
    try {
      const storedImage = await this.db.images.get(id)
      return storedImage?.blob || null
    } catch (error) {
      logger.error('[ImageStore] Failed to get image blob:', error)
      return null
    }
  }

  /**
   * サムネイルBlobを取得（URL生成用）
   */
  async getThumbnailBlob(id: string): Promise<Blob | null> {
    try {
      const storedImage = await this.db.images.get(id)
      return storedImage?.thumbnail || null
    } catch (error) {
      logger.error('[ImageStore] Failed to get thumbnail blob:', error)
      return null
    }
  }

  // Public method removed - using private method only

  /**
   * 全画像をMapとして取得
   */
  async getAllImages(): Promise<Map<string, string>> {
    try {
      const images = await this.db.images.toArray()
      const imageMap = new Map<string, string>()
      
      for (const image of images) {
        const base64 = await this.blobToBase64(image.blob)
        imageMap.set(image.id, base64)
      }
      
      return imageMap
      
    } catch (error) {
      logger.error('[ImageStore] Failed to get all images:', error)
      return new Map()
    }
  }

  /**
   * サムネイルを取得
   */
  async getThumbnail(id: string): Promise<string | null> {
    try {
      const storedImage = await this.db.images.get(id)
      
      if (!storedImage?.thumbnail) {
        return null
      }
      
      return this.blobToBase64(storedImage.thumbnail)
      
    } catch (error) {
      logger.error('[ImageStore] Failed to get thumbnail:', error)
      return null
    }
  }

  /**
   * 画像を削除
   */
  async deleteImage(id: string): Promise<void> {
    try {
      await this.db.images.delete(id)
      // Image deleted: ${id}
    } catch (error) {
      logger.error('[ImageStore] Failed to delete image:', error)
      throw error
    }
  }

  /**
   * 全画像をクリア
   */
  async clear(): Promise<void> {
    try {
      await this.db.images.clear()
      // Database cleared
    } catch (error) {
      logger.error('[ImageStore] Failed to clear database:', error)
      throw error
    }
  }

  /**
   * ストレージ使用量を取得
   */
  async getStorageInfo(): Promise<{ count: number; used: number }> {
    try {
      const images = await this.db.images.toArray()
      const count = images.length
      const used = images.reduce((sum, img) => {
        return sum + img.blob.size + (img.thumbnail?.size || 0)
      }, 0)
      
      return { count, used }
      
    } catch (error) {
      logger.error('[ImageStore] Failed to get storage info:', error)
      return { count: 0, used: 0 }
    }
  }

  /**
   * 複数画像を一括保存（Dexieのトランザクション使用）
   */
  async saveMultipleImages(images: Array<{ id?: string; base64Data: string }>): Promise<string[]> {
    const savedIds: string[] = []
    
    await this.db.transaction('rw', this.db.images, async () => {
      for (const image of images) {
        const id = await this.saveImage(image.base64Data, image.id)
        savedIds.push(id)
      }
    })
    
    return savedIds
  }

  /**
   * 条件に基づいて画像を検索（Dexieのクエリ機能）
   */
  async findImages(options: {
    startDate?: Date
    endDate?: Date
    limit?: number
  }): Promise<StoredImage[]> {
    let query = this.db.images.orderBy('createdAt')
    
    if (options.startDate || options.endDate) {
      query = query.filter(image => {
        if (options.startDate && image.createdAt < options.startDate) return false
        if (options.endDate && image.createdAt > options.endDate) return false
        return true
      })
    }
    
    if (options.limit) {
      query = query.limit(options.limit)
    }
    
    return await query.toArray()
  }

  // ヘルパーメソッド

  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  private async generateThumbnail(base64Data: string): Promise<Blob | undefined> {
    try {
      // Canvas APIを使用してサムネイル生成
      const img = new Image()
      
      return new Promise((resolve) => {
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            resolve(undefined)
            return
          }
          
          // サムネイルサイズ（最大200x200）
          const maxSize = 200
          let width = img.width
          let height = img.height
          
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width
              width = maxSize
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height
              height = maxSize
            }
          }
          
          canvas.width = width
          canvas.height = height
          ctx.drawImage(img, 0, 0, width, height)
          
          canvas.toBlob((blob) => {
            resolve(blob || undefined)
          }, 'image/jpeg', 0.8)
        }
        
        img.onerror = () => resolve(undefined)
        img.src = base64Data
      })
    } catch (error) {
      logger.error('[ImageStore] Failed to generate thumbnail:', error)
      return undefined
    }
  }

  private async getImageMetadata(base64Data: string): Promise<StoredImage['metadata'] | undefined> {
    try {
      return new Promise((resolve) => {
        const img = new Image()
        
        img.onload = () => {
          const headerPart = base64Data.split(',')[0]
          const mimeTypeMatch = headerPart?.match(/:(.*?);/)
          const mimeType = mimeTypeMatch?.[1] || 'image/jpeg'
          
          resolve({
            width: img.width,
            height: img.height,
            mimeType
          })
        }
        
        img.onerror = () => resolve(undefined)
        img.src = base64Data
      })
    } catch (error) {
      logger.error('[ImageStore] Failed to get image metadata:', error)
      return undefined
    }
  }
}